import React, { useState, useEffect } from "react";
import { getAllUserActions, getUserActionsByStoryId, getAllGameStories } from "../../utils/api";
import LoadingState from "../../components/LoadingState";

const UserActions = () => {
  const [actions, setActions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");
  const [page, setPage] = useState(1);
  const [limit] = useState(50);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userSummaries, setUserSummaries] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [stories, setStories] = useState([]);
  const [selectedStoryId, setSelectedStoryId] = useState("");
  
  useEffect(() => {
    fetchStories();
  }, []);
  
  useEffect(() => {
    fetchUserActions();
  }, [page, limit, selectedStoryId]);
  
  const fetchStories = async () => {
    try {
      const response = await getAllGameStories();
      console.log("Stories response:", response);
      if (response.success) {
        setStories(response.data);
        console.log("Stories loaded:", response.data);
      } else {
        console.error("Failed to fetch stories:", response.message);
      }
    } catch (error) {
      console.error("Error fetching stories:", error);
    }
  };
  
  const fetchUserActions = async () => {
    setIsLoading(true);
    setError("");
    
    try {
      let response;
      if (selectedStoryId) {
        response = await getUserActionsByStoryId(selectedStoryId, page, limit);
      } else {
        response = await getAllUserActions(page, limit);
      }
      
      if (response.success) {
        setActions(response.data);
        setTotalPages(response.totalPages || 1);
        
        // Group actions by user
        const userMap = new Map();
        
        response.data.forEach(action => {
          const userId = action.userId;
          if (!userId) return;
          
          if (!userMap.has(userId)) {
            // Initialize user data
            userMap.set(userId, {
              userId,
              userName: action.userDetails?.guestName || action.userDetails?.username || "不明",
              roomNumber: action.userDetails?.roomNumber || "-",
              actions: [],
              lastActionTimestamp: action.timestamp,
              status: "進行中", // Default to "in progress"
              questionIndexes: new Set() // Track unique question indexes
            });
          }
          
          const userData = userMap.get(userId);
          
          // Add action to user's actions
          userData.actions.push(action);
          
          // Track unique question indexes
          if (typeof action.questionIndex === 'number') {
            userData.questionIndexes.add(action.questionIndex);
          }
          
          // Check for completion indicators in the action details
          if (
            action.actionType === 'game_complete' || 
            (action.actionDetails && (
              action.actionDetails.gameCompleted === true ||
              action.actionDetails.complete === true ||
              (action.actionDetails.text && 
               typeof action.actionDetails.text === 'string' &&
               action.actionDetails.text.includes('おめでとう'))
            ))
          ) {
            userData.status = "完了"; // Mark as completed
          }
          
          // Update last action timestamp if this action is newer
          if (new Date(action.timestamp) > new Date(userData.lastActionTimestamp)) {
            userData.lastActionTimestamp = action.timestamp;
          }
        });
        
        // Determine status based on question count if no explicit completion
        Array.from(userMap.values()).forEach(userData => {
          // If user has answered more than 10 questions, consider as completed
          if (userData.status !== "完了" && userData.questionIndexes.size >= 10) {
            userData.status = "完了";
          }
        });
        
        // Sort users by latest action timestamp (newest first)
        const sortedUsers = Array.from(userMap.values())
          .sort((a, b) => new Date(b.lastActionTimestamp) - new Date(a.lastActionTimestamp));
        
        setUserSummaries(sortedUsers);
      } else {
        setError(response.message || "Failed to load user actions");
      }
    } catch (err) {
      console.error("Error fetching user actions:", err);
      setError(`ユーザーアクションの読み込みに失敗しました: ${err.message || "不明なエラー"}`);
    } finally {
      setIsLoading(false);
    }
  };

  // Function to format action details for display
  const formatActionDetails = (action) => {
    const details = action.actionDetails;
    
    if (!details) return "-";
    
    switch(action.actionType) {
      case 'text_input':
        return `入力: "${details.userAnswer}" (${details.isCorrect ? '✓正解' : '✗不正解'})`;
      case 'option_select':
        return `選択: "${details.selectedOption}" (${details.isCorrect ? '✓正解' : '✗不正解'})`;
      case 'file_upload':
        return `ファイル: ${details.fileName} (${details.fileSize} bytes)`;
      case 'show_answer':
        return `問題: "${details.questionText?.substring(0, 30)}${details.questionText?.length > 30 ? '...' : ''}"`;
      case 'hint_request':
        return `問題: "${details.questionText?.substring(0, 30)}${details.questionText?.length > 30 ? '...' : ''}"`;
      default:
        return JSON.stringify(details);
    }
  };

  // Function to format action type for display
  const formatActionType = (type) => {
    const actionTypes = {
      'text_input': 'テキスト入力',
      'option_select': '選択肢選択',
      'file_upload': 'ファイルアップロード',
      'show_answer': '正解表示',
      'hint_request': 'ヒント表示'
    };
    
    return actionTypes[type] || type;
  };

  const handleNextPage = () => {
    if (page < totalPages) {
      setPage(page + 1);
    }
  };

  const handlePrevPage = () => {
    if (page > 1) {
      setPage(page - 1);
    }
  };
  
  const handleUserSelect = (user) => {
    setSelectedUser(selectedUser?.userId === user.userId ? null : user);
  };
  
  // Filter users based on search term and status filter
  const filteredUsers = userSummaries.filter(user => {
    // Filter by search term
    const matchesSearch = 
      searchTerm === "" || 
      user.userName.toLowerCase().includes(searchTerm.toLowerCase()) || 
      user.roomNumber.toLowerCase().includes(searchTerm.toLowerCase());
    
    // Filter by status
    const matchesStatus = 
      statusFilter === "all" || 
      (statusFilter === "completed" && user.status === "完了") ||
      (statusFilter === "inprogress" && user.status === "進行中");
    
    return matchesSearch && matchesStatus;
  });
  
  // Add a debugging display during development
  const debugInfo = process.env.NODE_ENV === 'development' && stories ? (
    <div className="mb-2 text-xs text-gray-500">
      {stories.length} stories loaded. {stories.map(s => s.title).join(', ')}
    </div>
  ) : null;

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-800">ユーザーアクション</h1>
        <p className="text-gray-600 mt-1">ユーザーのアクションログを表示</p>
      </div>
      
      <div className="bg-white rounded-lg shadow p-6">
        {debugInfo}
      
      {isLoading ? (
        <LoadingState message="ユーザーアクションを読み込み中..." />
      ) : error ? (
        <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-4">
          <div className="flex">
            <div className="ml-3">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          </div>
        </div>
      ) : (
        <div>
          <h2 className="text-lg font-semibold mb-4">ユーザー一覧</h2>
          
          {userSummaries.length === 0 ? (
            <p className="text-gray-500">記録されたユーザーはありません。</p>
          ) : (
            <>
              {/* Search and filter controls */}
              <div className="mb-4 flex flex-wrap gap-3">
                <div className="flex-grow relative">
                  <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                    <svg className="w-4 h-4 text-gray-500" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <input 
                    type="text"
                    placeholder="ユーザー名や部屋番号で検索..."
                    className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-500 focus:border-gray-500"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>
                
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                    <svg className="w-4 h-4 text-gray-500" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <select 
                    className="pl-10 pr-10 py-2 border border-gray-300 rounded-md appearance-none cursor-pointer focus:outline-none focus:ring-2 focus:ring-gray-500 focus:border-gray-500"
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                  >
                    <option value="all">全てのユーザー</option>
                    <option value="completed">完了したユーザー</option>
                    <option value="inprogress">進行中のユーザー</option>
                  </select>
                  <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                    <svg className="w-5 h-5 text-gray-600" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                  </div>
                </div>
                
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                    <svg className="w-4 h-4 text-gray-500" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <select
                    className="pl-10 pr-10 py-2 min-w-[200px] border border-blue-400 rounded-md bg-blue-50 text-blue-800 appearance-none cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    value={selectedStoryId}
                    onChange={(e) => {
                      setSelectedStoryId(e.target.value);
                      setPage(1); // Reset to first page when changing story
                    }}
                    style={{ textOverflow: 'ellipsis' }}
                  >
                    <option value="">全てのストーリー</option>
                    {stories && stories.length > 0 ? (
                      stories.map((story) => (
                        <option key={story.storyId} value={story.storyId}>
                          {story.title || `ストーリー ${story.storyId}`}
                        </option>
                      ))
                    ) : (
                      <option disabled>ストーリーを読み込み中...</option>
                    )}
                  </select>
                  <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                    <svg className="w-5 h-5 text-blue-600" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                  </div>
                </div>
                
                <div>
                  <button 
                    onClick={fetchUserActions}
                    className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                  >
                    更新
                  </button>
                </div>
              </div>
              
              {/* User count display */}
              <div className="mb-2 text-sm text-gray-500">
                {filteredUsers.length}人のユーザーが表示されています
                {searchTerm && ` (検索: "${searchTerm}")`}
                {statusFilter !== "all" && ` (${statusFilter === "completed" ? "完了" : "進行中"})`}
                {selectedStoryId && ` (ストーリー: "${stories.find(s => s.storyId === selectedStoryId)?.title || selectedStoryId}")`}
              </div>
              
              {/* User summary table */}
              <div className="overflow-x-auto mb-8">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">ユーザー名</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">部屋番号</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">最終アクション</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">状態</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">アクション数</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredUsers.map(user => (
                      <tr 
                        key={user.userId} 
                        className={`hover:bg-gray-50 cursor-pointer ${selectedUser?.userId === user.userId ? 'bg-blue-50' : ''}`}
                        onClick={() => handleUserSelect(user)}
                      >
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {user.userName}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {user.roomNumber}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {new Date(user.lastActionTimestamp).toLocaleString("ja-JP")}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          <span className={`px-2 py-1 rounded-full text-xs ${user.status === '完了' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>
                            {user.status}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {user.actions.length}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              
              {/* User action details when a user is selected */}
              {selectedUser && (
                <div className="mt-6 border-t border-gray-200 pt-4">
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="text-lg font-medium">
                      {selectedUser.userName}さんのアクション履歴
                    </h3>
                    <button 
                      onClick={() => setSelectedUser(null)}
                      className="text-sm text-gray-500 hover:text-gray-700"
                    >
                      閉じる
                    </button>
                  </div>
                  
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">タイムスタンプ</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">アクションタイプ</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">詳細</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {selectedUser.actions
                          .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp)) // Sort by newest first
                          .map(action => (
                          <tr key={action._id} className="hover:bg-gray-50">
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {new Date(action.timestamp).toLocaleString("ja-JP")}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {formatActionType(action.actionType)}
                            </td>
                            <td className="px-6 py-4 whitespace-normal text-sm text-gray-500 max-w-sm">
                              {formatActionDetails(action)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
              
              <div className="mt-4 flex justify-between">
                <button
                  onClick={handlePrevPage}
                  disabled={page === 1}
                  className={`px-4 py-2 rounded ${page === 1 ? "bg-gray-300 cursor-not-allowed" : "bg-blue-500 hover:bg-blue-600 text-white"}`}
                >
                  前へ
                </button>
                <span className="px-4 py-2">
                  {page} / {totalPages}
                </span>
                <button
                  onClick={handleNextPage}
                  disabled={page === totalPages}
                  className={`px-4 py-2 rounded ${page === totalPages ? "bg-gray-300 cursor-not-allowed" : "bg-blue-500 hover:bg-blue-600 text-white"}`}
                >
                  次へ
                </button>
              </div>
            </>
          )}
          </div>
        )}
      </div>
    </div>
  );
};

export default UserActions;
